import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { NetworkFixedCdCard, PeriodSelector } from '@/components/analytics'
import { LoadingScreen } from '@/components/LoadingScreen'
import { RatingsBoard } from '@/components/ratings/RatingsBoard'
import { getSupabase } from '@/lib/supabase'
import { withAuthRecovery } from '@/lib/authRecovery'
import { getLocalIsoDate } from '@/lib/analytics/periodCalculations'
import {
  buildEmptyDataset,
  findSelfScopeKeys,
  loadUnifiedManagementRatings,
  type UnifiedRatingsData,
} from '@/lib/ratings/unifiedRatings'
import { getCurrentWeekNetworkFixedCd, type CurrentWeekNetworkFixedCdSnapshot } from '@/services/networkFixedCdService'
import { useRatingsFiltersStore, useStudioStore, type RatingsPeriodRangeKey } from '@/store'
import type { MetricPeriod } from '@/lib/utils'

type MetricWeek = {
  week_id: number
  period_id: number
  week_number_in_period: number
  week_start: string
  week_end: string
  week_len_days: number
}

const RATINGS_STUDIO_STATUSES = new Set(['OPEN', 'SUSPENDED', 'SERVICE'])

export function RatingsPage() {
  const { partnerNumber, studioId } = useParams<{ partnerNumber?: string; studioId: string }>()
  const { partners, getPartnerStudios, setSelectedPartnerNumber } = useStudioStore()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentPeriod, setCurrentPeriod] = useState<MetricPeriod | null>(null)
  const [availablePeriods, setAvailablePeriods] = useState<MetricPeriod[]>([])
  const [currentPeriodIndex, setCurrentPeriodIndex] = useState(0)
  const [metricWeeks, setMetricWeeks] = useState<MetricWeek[]>([])
  const selectedRangeKey = useRatingsFiltersStore((state) => state.selectedRangeKey)
  const setSelectedRangeKey = useRatingsFiltersStore((state) => state.setSelectedRangeKey)
  const setSelectedPeriodId = useRatingsFiltersStore((state) => state.setSelectedPeriodId)
  const [data, setData] = useState<UnifiedRatingsData>(() => buildEmptyDataset())
  const [dataLoading, setDataLoading] = useState(true)
  const [networkFixedCd, setNetworkFixedCd] = useState<CurrentWeekNetworkFixedCdSnapshot | null>(null)

  const parsedPartnerNumber = useMemo(() => {
    const value = Number(partnerNumber)
    return Number.isNaN(value) ? null : value
  }, [partnerNumber])

  const studioIntId = useMemo(() => {
    const value = Number(studioId)
    return Number.isNaN(value) ? null : value
  }, [studioId])

  const partnerStudios = useMemo(
    () => getPartnerStudios(parsedPartnerNumber).filter((studio) =>
      RATINGS_STUDIO_STATUSES.has(studio.status_code || 'OPEN')
    ),
    [getPartnerStudios, parsedPartnerNumber]
  )

  const currentStudio = useMemo(
    () => partnerStudios.find((studio) => studio.studio_internal_id === studioIntId) || null,
    [partnerStudios, studioIntId]
  )

  const partnerLabelMap = useMemo(
    () => new Map(partners.map((partner) => [partner.partner_number, partner.label])),
    [partners]
  )

  const currentPartnerNumber = currentStudio?.partner_number ?? parsedPartnerNumber ?? null

  useEffect(() => {
    setSelectedPartnerNumber(parsedPartnerNumber)
  }, [parsedPartnerNumber, setSelectedPartnerNumber])

  useEffect(() => {
    const loadPeriods = async () => {
      if (!studioIntId) {
        setAvailablePeriods([])
        setCurrentPeriod(null)
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const supabase = getSupabase()
        const today = getLocalIsoDate()
        const { data: rows, error: periodsError } = await withAuthRecovery(() =>
          supabase
            .from('metric_periods')
            .select('*')
            .lte('period_start', today)
            .order('period_start', { ascending: true })
        ) as { data: MetricPeriod[] | null; error: { message?: string } | null }

        if (periodsError || !rows || rows.length === 0) {
          setAvailablePeriods([])
          setCurrentPeriod(null)
          return
        }

        const rememberedPeriodId = useRatingsFiltersStore.getState().selectedPeriodId
        const rememberedIndex = rememberedPeriodId == null
          ? -1
          : rows.findIndex((period) => period.period_id === rememberedPeriodId)
        const currentIndex = rows.findIndex((period) => period.period_start <= today && period.period_end >= today)
        const selectedIndex = rememberedIndex >= 0
          ? rememberedIndex
          : currentIndex >= 0
            ? currentIndex
            : rows.length - 1
        setAvailablePeriods(rows)
        setCurrentPeriodIndex(selectedIndex)
        setCurrentPeriod(rows[selectedIndex])
        setSelectedPeriodId(rows[selectedIndex].period_id)
      } finally {
        setLoading(false)
      }
    }

    loadPeriods()
  }, [setSelectedPeriodId, studioIntId])

  useEffect(() => {
    if (!currentPeriod?.period_id) {
      setMetricWeeks([])
      return
    }

    let cancelled = false
    const supabase = getSupabase()
    withAuthRecovery(() =>
      supabase
        .from('metric_weeks')
        .select('week_id, period_id, week_number_in_period, week_start, week_end, week_len_days')
        .eq('period_id', currentPeriod.period_id)
        .order('week_number_in_period', { ascending: true })
    ).then(({ data: rows }) => {
      if (!cancelled) setMetricWeeks((rows as MetricWeek[]) || [])
    })

    return () => {
      cancelled = true
    }
  }, [currentPeriod?.period_id])

  const effectivePeriodEnd = useMemo(() => {
    if (!currentPeriod) return null
    const today = getLocalIsoDate()
    return today < currentPeriod.period_end ? today : currentPeriod.period_end
  }, [currentPeriod])

  const yesterdayInCurrentPeriod = useMemo(() => {
    if (!currentPeriod) return null
    const today = getLocalIsoDate()
    if (!(currentPeriod.period_start <= today && currentPeriod.period_end >= today)) return null
    const rawYesterday = new Date(`${today}T00:00:00`)
    rawYesterday.setDate(rawYesterday.getDate() - 1)
    const yesterday = getLocalIsoDate(rawYesterday)
    if (yesterday < currentPeriod.period_start || yesterday > currentPeriod.period_end) return null
    return yesterday
  }, [currentPeriod])

  const selectedWeekNumber = selectedRangeKey.startsWith('week-') ? Number(selectedRangeKey.replace('week-', '')) : null
  const selectedMetricWeek = useMemo(
    () => (selectedWeekNumber ? metricWeeks.find((week) => week.week_number_in_period === selectedWeekNumber) || null : null),
    [metricWeeks, selectedWeekNumber]
  )

  const rangeOptions = useMemo(() => {
    const visibleWeeks = metricWeeks
      .filter((week) => !effectivePeriodEnd || week.week_start <= effectivePeriodEnd)
      .sort((a, b) => a.week_number_in_period - b.week_number_in_period)

    return [
      { value: 'period' as RatingsPeriodRangeKey, label: 'Весь период' },
      ...(yesterdayInCurrentPeriod ? [{ value: 'yesterday' as RatingsPeriodRangeKey, label: 'За вчера' }] : []),
      ...visibleWeeks.map((week) => ({
        value: `week-${week.week_number_in_period}` as RatingsPeriodRangeKey,
        label: `Неделя ${week.week_number_in_period}`
      }))
    ]
  }, [effectivePeriodEnd, metricWeeks, yesterdayInCurrentPeriod])

  useEffect(() => {
    if (!rangeOptions.some((option) => option.value === selectedRangeKey)) {
      setSelectedRangeKey('period')
    }
  }, [rangeOptions, selectedRangeKey, setSelectedRangeKey])

  const selectedRange = useMemo(() => {
    if (!currentPeriod || !effectivePeriodEnd) return null
    if (selectedRangeKey === 'yesterday' && yesterdayInCurrentPeriod) {
      return { mode: 'yesterday' as const, start: yesterdayInCurrentPeriod, end: yesterdayInCurrentPeriod, label: 'За вчера' }
    }
    if (!selectedMetricWeek) {
      return { mode: 'period' as const, start: currentPeriod.period_start, end: effectivePeriodEnd, label: 'Весь период' }
    }
    return {
      mode: 'week' as const,
      start: selectedMetricWeek.week_start,
      end: selectedMetricWeek.week_end < effectivePeriodEnd ? selectedMetricWeek.week_end : effectivePeriodEnd,
      label: `Неделя ${selectedMetricWeek.week_number_in_period}`
    }
  }, [currentPeriod, effectivePeriodEnd, selectedMetricWeek, selectedRangeKey, yesterdayInCurrentPeriod])

  useEffect(() => {
    let isActive = true

    const loadNetworkFixedCd = async () => {
      if (!currentPeriod || !selectedRange || selectedRange.mode !== 'week' || !selectedWeekNumber) {
        setNetworkFixedCd(null)
        return
      }

      const today = getLocalIsoDate()
      const isCurrentWeek = selectedRange.start <= today && selectedRange.end >= today
      if (!isCurrentWeek) {
        setNetworkFixedCd(null)
        return
      }

      try {
        const snapshot = await getCurrentWeekNetworkFixedCd(currentPeriod.period_id, selectedWeekNumber)
        if (isActive) {
          setNetworkFixedCd(snapshot)
        }
      } catch (err) {
        if (isActive) {
          console.error('RatingsPage network fixed cd:', err)
          setNetworkFixedCd(null)
        }
      }
    }

    loadNetworkFixedCd()

    return () => {
      isActive = false
    }
  }, [currentPeriod, selectedRange, selectedWeekNumber])

  useEffect(() => {
    let isActive = true

    const loadRatings = async () => {
      if (!currentPeriod || !selectedRange || partnerStudios.length === 0) return

      try {
        setDataLoading(true)
        setError(null)

        const today = getLocalIsoDate()
        const clientRangeEnd = selectedRange.mode === 'yesterday'
          ? selectedRange.end
          : selectedMetricWeek
            ? (selectedMetricWeek.week_end < today ? selectedMetricWeek.week_end : today)
            : (currentPeriod.period_end < today ? currentPeriod.period_end : today)
        const clientRangeStart = selectedRange.mode === 'yesterday'
          ? selectedRange.start
          : selectedMetricWeek
            ? selectedMetricWeek.week_start
            : currentPeriod.period_start

        const result = await loadUnifiedManagementRatings({
          studios: partnerStudios,
          partnerLabelMap,
          period: currentPeriod,
          rangeStart: selectedRange.start,
          rangeEnd: selectedRange.end,
          clientRangeStart,
          clientRangeEnd,
          rangeMode: selectedRange.mode,
          selectedWeekId: selectedMetricWeek?.week_id ?? null,
          accessScope: 'published',
        })

        if (!isActive) return
        setData(result)
      } catch (err) {
        if (!isActive) return
        console.error('RatingsPage:', err)
        setError(err instanceof Error ? err.message : 'Не удалось загрузить рейтинги')
      } finally {
        if (isActive) setDataLoading(false)
      }
    }

    loadRatings()
    return () => {
      isActive = false
    }
  }, [currentPeriod, partnerLabelMap, partnerStudios, selectedMetricWeek?.week_id, selectedRange])

  const selfKeys = useMemo(
    () => (studioIntId != null ? findSelfScopeKeys(data, studioIntId) : {}),
    [data, studioIntId]
  )
  const handlePeriodChange = (direction: 'prev' | 'next') => {
    const newIndex = direction === 'prev' ? currentPeriodIndex - 1 : currentPeriodIndex + 1
    if (newIndex >= 0 && newIndex < availablePeriods.length) {
      setDataLoading(true)
      setCurrentPeriodIndex(newIndex)
      setCurrentPeriod(availablePeriods[newIndex])
      setSelectedPeriodId(availablePeriods[newIndex].period_id)
      setSelectedRangeKey('period')
    }
  }

  const handleRangeChange = (value: RatingsPeriodRangeKey) => {
    if (value === selectedRangeKey) return
    setDataLoading(true)
    setSelectedRangeKey(value)
  }

  const canGoNext = () => {
    if (currentPeriodIndex >= availablePeriods.length - 1) return false
    const nextPeriod = availablePeriods[currentPeriodIndex + 1]
    const today = getLocalIsoDate()
    return Boolean(nextPeriod && nextPeriod.period_start <= today)
  }

  const sideHeader = useMemo(() => {
    if (!currentPeriod) return null

    return (
      <div className="space-y-2">
        <div className="rounded-xl border border-border/60 bg-muted/20 px-3 py-2">
          <PeriodSelector
            currentPeriod={currentPeriod}
            onPrevPeriod={() => handlePeriodChange('prev')}
            onNextPeriod={() => handlePeriodChange('next')}
            canGoPrev={currentPeriodIndex > 0}
            canGoNext={canGoNext()}
            showViewModeSelector
            isCurrentPeriod={currentPeriod.period_start <= getLocalIsoDate() && currentPeriod.period_end >= getLocalIsoDate()}
            periodFilterValue={selectedRangeKey}
            periodFilterOptions={rangeOptions}
            onPeriodFilterChange={(value) => handleRangeChange(value as RatingsPeriodRangeKey)}
            selectedRange={selectedRange}
            compact
            stacked
          />
        </div>
        {networkFixedCd ? (
          <NetworkFixedCdCard snapshot={networkFixedCd} compact />
        ) : null}
      </div>
    )
  }, [
    currentPeriod,
    currentPeriodIndex,
    networkFixedCd,
    rangeOptions,
    selectedRange,
    selectedRangeKey
  ])

  if (loading) {
    return <LoadingScreen />
  }

  if (!currentStudio || !currentPeriod) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4 text-sm text-muted-foreground">
        Не удалось определить студию или период для рейтинга.
      </div>
    )
  }

  if (dataLoading) {
    return <LoadingScreen />
  }

  return (
    <div className="space-y-3">
      {error ? (
        <div className="rounded-2xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      ) : null}

      <RatingsBoard
        data={data}
        mode="self"
        topN={10}
        compact
        highlight={{ studioId: currentStudio.studio_internal_id, partnerNumber: currentPartnerNumber ?? undefined }}
        selfKeys={{ ...selfKeys, partner: currentPartnerNumber != null ? String(currentPartnerNumber) : selfKeys.partner }}
        sideHeader={sideHeader}
      />

    </div>
  )
}
